import { getWebPath } from '@doss/common-all'
import { Skeleton } from '@mui/material'
import { FiFile, FiLayout } from 'react-icons/fi'
import { RxLightningBolt } from 'react-icons/rx'
import { TbColumns } from 'react-icons/tb'
import { NavigateFunction } from 'react-router'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { SearchResult } from '~/components/navigation/types'
import { BreadcrumbObjectType, DataType } from '../generated/apollo-operations'

/**
 * Function to get the label for an option (no callback needed)
 */
export function getOptionLabel(option: SearchResult) {
  switch (option.__typename) {
    case 'TableRow': {
      return option.name
    }
    case 'Table':
      return option.name
    case 'Document':
      return option.name
    case 'Form':
    case 'FormV2':
      if (option.buttons.length > 0) {
        return `${option.buttons[0].name} (${option.name})`
      }
      return option.name
  }
}

/**
 * Function to calculate the number of matches for an option
 * This allows us to sort the options by the number of matches
 */
export function getMatchScore(option: SearchResult, searchQuery: string) {
  const label = getOptionLabel(option)

  // If there is no label, return 0 since there is no match
  if (!label) {
    return 0
  }

  /**
   * Score the matches in the label, and give a higher score to forms (2x) since those routes are
   * "Actions" for our users (i.e. "Create PO form")
   */
  const lowerCaseLabel = label.toLowerCase()
  const lowerCaseSearchQuery = searchQuery.toLowerCase()

  // Count the number of occurences of the search auery in the label
  const matches = lowerCaseLabel.split(lowerCaseSearchQuery).length - 1

  // If the option is a form, double the matches since these are likely actions taken by the user
  if (option.__typename === 'Form' || option.__typename === 'FormV2') {
    return matches * 2
  }

  return matches
}

/**
 * Function to get the icon for an option based on its type
 */
export function getSearchIconByOptionType(option: SearchResult) {
  switch (option.__typename) {
    case 'TableRow':
      return <TbColumns fontSize={'18px'} />
    case 'Table':
      return <FiLayout fontSize={'18px'} />
    case 'Document':
      return <FiFile fontSize={'18px'} />
    case 'Form':
    case 'FormV2':
      return <RxLightningBolt fontSize={'18px'} />
  }
}

/*
 * Function to find the cell that matches the search query, if any, and return it
 */
export function getMatchedCell(option: SearchResult, searchQuery: string) {
  if (option.__typename === 'TableRow') {
    const matchedCell = option.cells.find((cell) => {
      const lowerCaseCell = cell.userVisibleValue?.toLowerCase()
      const lowerCaseSearchQuery = searchQuery.toLowerCase()
      return lowerCaseCell?.includes(lowerCaseSearchQuery)
    })

    return {
      matchedColumnName: matchedCell?.column.name,
      matchedColumnType: matchedCell?.column.type ?? DataType.STRING,
      matchedCellData: matchedCell?.userVisibleValue ?? undefined,
    }
  }

  return {
    matchedColumnName: undefined,
    matchedColumnType: DataType.ID,
    matchedCellData: undefined,
  }
}

/**
 * Function to push a selected option onto the router url
 */
export function pushSelectedOptionToRouter(option: SearchResult, navigate: NavigateFunction) {
  switch (option.__typename) {
    case 'Table':
      navigate(getWebPath({ objectType: BreadcrumbObjectType.TABLE, objectId: option.id }))
      break
    case 'TableRow':
      if (option.parentRow) {
        navigate(
          getWebPath({
            objectType: BreadcrumbObjectType.TABLE_ROW,
            objectId: option.parentRow.id,
          }),
        )
        break
      }
      navigate(getWebPath({ objectType: BreadcrumbObjectType.TABLE_ROW, objectId: option.id }))
      break
    case 'Document':
      navigate(getWebPath({ objectType: BreadcrumbObjectType.DOCUMENT, objectId: option.id }))
      break
    case 'Form':
    case 'FormV2':
      navigate(getWebPath({ objectType: BreadcrumbObjectType.FORM, objectId: option.id }))
      break
  }
}

/**
 * Function to determine the loading and skeleton state of the search input
 */
export function getNoOptionsText(
  t: (key: TranslationKeys) => string,
  loading: boolean,
  searchQuery: string,
) {
  if (loading) {
    return <Skeleton variant="text" width={100} />
  }
  if (searchQuery.length > 0) {
    return t('search.no_results')
  }
  return ''
}

/**
 * Function that splits the result by the matched string (as a regex).
 * Regex 'gi' for global + case-insensitive and escape the search query.
 */
export const splitByQuery = (text: string, searchQuery: string, usingLiveSearch: boolean) => {
  const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return usingLiveSearch ? text.split(regex) : [text]
}

// Strips the search result option after this many characters
const OPTION_CHAR_LIMIT = 60

// The number of characters to show on either side of the match
const SLICE_WINDOW_SIZE = 30

/*
This logic ensures that a portion of the matched cell data is displayed, focusing on the
matched text. If the cell data exceeds a certain length (OPTION_CHAR_LIMIT in this),
it slices the text to show the match in the middle with ellipses indicating omitted
text. The slicing is balanced to allocate any remaining character budget from one side
to the other, ensuring that the visible portion is centered around the match as much as
possible.
*/
export function handleLongText(
  text: string,
  searchQuery: string,
  usingLiveSearch: boolean,
  sliceWindowSize: number = SLICE_WINDOW_SIZE,
  optionalCharLimit: number = OPTION_CHAR_LIMIT,
) {
  if (usingLiveSearch && text.length > optionalCharLimit) {
    // The index of the first occurrence of the search query in the cell data.
    const matchIndex = text.toLowerCase().indexOf(searchQuery.toLowerCase())

    /**
     * The starting index for slicing, calculated to be `SLICE_WINDOW_SIZE`
     * characters before the match, but not less than 0. If there are unused
     * characters on the end side, they are added to this side.
     */
    let sliceStart = Math.max(0, matchIndex - sliceWindowSize)

    /**
     * The ending index for slicing, calculated to be `SLICE_WINDOW_SIZE`
     * characters after the end of the match, but not more than the length of
     * the cell data. If there are unused characters on the start side, they
     * are added to this side.
     */
    let sliceEnd = Math.min(text.length, matchIndex + searchQuery.length + sliceWindowSize)

    // The number of unused characters available for reallocation
    const remainingStart =
      sliceStart > 0 ? sliceWindowSize - (matchIndex - sliceStart) : sliceWindowSize

    /**
     * The number of unused characters available for reallocation
     */
    const remainingEnd =
      sliceEnd < text.length
        ? sliceWindowSize - (sliceEnd - matchIndex - searchQuery.length)
        : sliceWindowSize

    sliceStart = Math.max(0, sliceStart - remainingEnd)
    sliceEnd = Math.min(text.length, sliceEnd + remainingStart)

    return (
      (sliceStart > 0 ? '...' : '') +
      text.slice(sliceStart, sliceEnd) +
      (sliceEnd < text.length ? '...' : '')
    )
  }
  return text
}
